import {
  Activity,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type ReactNode,
} from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useMatches } from 'react-router-dom';
import { Loading } from './components/EmptyState';
import { RouteError } from './components/RouteError';
import { RouteAccess } from './components/RouteAccess';
import { PublicFrame, Shell } from './components/Shell';
import { PERSON_KEYS, queryClient, useSession } from './lib/queries';
import { legacyLibraryTarget } from './lib/legacy-library';
import { hasProgrammeAccess, isAccountMode } from '@/shared/contracts';
import { useTenant } from './lib/tenant-context';
import { apiEvents, isApiProblem, REVIEW_REQUIRED } from './lib/api';
// Sign-in and Offline ship in the main bundle: they are the two screens that must render when a
// lazy chunk cannot be fetched.
import Offline from './pages/public/Offline';
import SignIn from './pages/public/SignIn';

const publicLoading = (
  <PublicFrame>
    <Loading />
  </PublicFrame>
);

/** Keep a public main landmark while its lazy page or session check is loading. */
function page(
  load: () => Promise<{ default: ComponentType }>,
  fallback: ReactNode = <Loading className="mx-auto mt-16 max-w-sm" />,
): ReactNode {
  const Page = lazy(load);
  return (
    <Suspense fallback={fallback}>
      <Page />
    </Suspense>
  );
}

/**
 * A route whose `handle` sets `personal` belongs to the person rather than to a programme. Once the
 * account layer is on, such a route needs authentication alone, so losing a membership no longer
 * bounces the person away from their own account. With the layer off it changes nothing.
 */
const isPersonalRoute = (handle: unknown): boolean =>
  typeof handle === 'object' && handle !== null && (handle as { personal?: unknown }).personal === true;

/**
 * Behind sign-in: a missing session goes to /sign-in with the way back; a session with no active
 * standing on this tenant goes to /not-enrolled. A superadmin's virtual membership passes. Every
 * guarded route shares this one guard and one frame, so moving between them never remounts it.
 */
function subscribeConnection(callback: () => void) {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}
function RequireSession({ children }: { children: ReactNode }) {
  const tenant = useTenant();
  const session = useSession();
  const location = useLocation();
  const personalRoute = useMatches().some((match) => isPersonalRoute(match.handle));
  const online = useSyncExternalStore(subscribeConnection, () => navigator.onLine);
  const [rechecking, setRechecking] = useState(!navigator.onLine);
  const [reviewRequired, setReviewRequired] = useState(false);
  const reviewVersion = useRef(0);
  const focused = useRef<HTMLElement | null>(null);
  const { refetch } = session;
  const retry = useCallback(() => {
    setRechecking(true);
    const version = reviewVersion.current;
    void refetch().then((result) => {
      // An older session request cannot dismiss a newer review-access refusal.
      if (result.isSuccess && version === reviewVersion.current) setReviewRequired(false);
      if (navigator.onLine) setRechecking(false);
    });
  }, [refetch]);
  useEffect(() => {
    const disconnected = () => setRechecking(true);
    const reviewExpired = () => {
      reviewVersion.current++;
      setReviewRequired(true);
    };
    apiEvents.addEventListener(REVIEW_REQUIRED, reviewExpired);
    window.addEventListener('offline', disconnected);
    window.addEventListener('online', retry);
    return () => {
      apiEvents.removeEventListener(REVIEW_REQUIRED, reviewExpired);
      window.removeEventListener('offline', disconnected);
      window.removeEventListener('online', retry);
    };
  }, [retry]);
  const s = session.data;
  const accountMode = isAccountMode(s);
  const personal = personalRoute && accountMode;
  const signedIn = !!(s?.authenticated && s.user);
  const allowed = personal ? signedIn : hasProgrammeAccess(s);
  const needsReview = reviewRequired || (isApiProblem(session.error) && session.error.reviewRequired);
  const blocked = !online || session.isError || rechecking || needsReview;
  useLayoutEffect(() => {
    if (blocked) return;
    const target = focused.current;
    const frame = requestAnimationFrame(() => {
      if (target?.isConnected && target.getClientRects().length) target.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [blocked]);
  useEffect(() => {
    if (session.isSuccess && !allowed) {
      // Access loss clears the programme. With the account layer on it leaves the person's own
      // data alone, so their account and profile are still there when they open Account.
      const keepers = accountMode ? PERSON_KEYS : ['session'];
      queryClient.removeQueries({ predicate: (q) => !keepers.includes(String(q.queryKey[0])) });
    }
  }, [allowed, session.isSuccess, accountMode]);
  if (session.isPending && !blocked) return publicLoading;
  if (session.isSuccess && !signedIn) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }
  if (session.isSuccess && !personal && !hasProgrammeAccess(s)) {
    return <Navigate to="/not-enrolled" replace />;
  }
  return (
    <>
      {blocked && (
        <Offline
          accessUnverified={online}
          checking={online && session.isFetching}
          reviewRequired={needsReview}
          onRetry={retry}
        />
      )}
      {allowed && (
        // Preserve drafts through a connection failure, pausing hidden effects and changing owner atomically.
        <Activity key={`${tenant.slug}:${s?.user?.id}`} mode={blocked ? 'hidden' : 'visible'}>
          <div
            inert={blocked}
            onFocusCapture={(event) => {
              if (!blocked) focused.current = event.target;
            }}
          >
            {children}
          </div>
        </Activity>
      )}
    </>
  );
}

function RequireRole({ role, children }: { role: 'operator_admin' | 'superadmin'; children: ReactNode }) {
  const session = useSession();
  const s = session.data;
  if (!s?.user) return null;
  const allowed =
    role === 'superadmin'
      ? s.user.is_superadmin
      : s.user.is_superadmin || s.membership?.role === 'operator_admin';
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Guarded() {
  return (
    <RequireSession>
      <Shell />
    </RequireSession>
  );
}

function SponsorArea() {
  return (
    <RequireRole role="operator_admin">
      <Outlet />
    </RequireRole>
  );
}

function AdminArea() {
  return (
    <RequireRole role="superadmin">
      <Outlet />
    </RequireRole>
  );
}

/**
 * Library (`/cards`) and a field card (`/cards/:id`) share one lazy page, so React keeps the same
 * instance when the learner moves between them and the page's scroll restoration stays mounted.
 */
const cardsPage = page(() => import('./pages/learner/Cards'));
const learnPage = page(() => import('./pages/learner/Library'));

/** Keep old shared Brief and Film links working after those rows moved from Learn to Library. */
function LearnRoute() {
  const { search } = useLocation();
  const target = legacyLibraryTarget(search);
  return target ? <Navigate to={target} replace /> : learnPage;
}

export const router = createBrowserRouter([
  {
    element: <RouteAccess />,
    children: [
      {
        path: '*',
        handle: { title: 'Page not found' },
        element: page(() => import('./pages/public/NotFound'), publicLoading),
        errorElement: <RouteError />,
      },
      { path: '/sign-in', handle: { title: 'Sign in' }, element: <SignIn />, errorElement: <RouteError /> },
      {
        element: <Shell />,
        errorElement: <RouteError />,
        children: [
          {
            path: '/explore',
            handle: { title: 'Explore' },
            element: page(() => import('./pages/public/Explore')),
          },
        ],
      },
      {
        path: '/check-email',
        handle: { title: 'Check your email' },
        element: page(() => import('./pages/public/CheckEmail'), publicLoading),
        errorElement: <RouteError />,
      },
      {
        path: '/not-enrolled',
        handle: { title: 'Not enrolled' },
        element: page(() => import('./pages/public/NotEnrolled'), publicLoading),
        errorElement: <RouteError />,
      },
      { path: '/offline', handle: { title: 'Offline' }, element: <Offline />, errorElement: <RouteError /> },
      {
        path: '/verify/:code',
        handle: { title: 'Verify credential', detail: true },
        element: page(() => import('./pages/public/Verify'), publicLoading),
        errorElement: <RouteError />,
      },
      {
        element: <Guarded />,
        errorElement: <RouteError />,
        children: [
          { path: '/', handle: { title: 'Home' }, element: page(() => import('./pages/learner/Home')) },
          {
            // Direction AC names (L-42): /library is Learn, /cards is Library, /credential is Record.
            path: '/library',
            handle: { title: 'Learn' },
            element: <LearnRoute />,
          },
          {
            path: '/learn/:id',
            handle: { title: 'Learning', detail: true },
            element: page(() => import('./pages/learner/Item')),
          },
          {
            path: '/learn/:id/check',
            handle: { title: 'Assessment', detail: true },
            element: page(() => import('./pages/learner/Attempt')),
          },
          {
            path: '/cards',
            handle: { title: 'Library', preserveScroll: true },
            element: cardsPage,
          },
          {
            // The same page instance as the shelf, so its one scroll restoration survives opening a
            // card and coming back (see `pages/learner/Cards.tsx`).
            path: '/cards/:id',
            handle: { title: 'Field card', detail: true, preserveScroll: true },
            element: cardsPage,
          },
          {
            path: '/credential',
            handle: { title: 'Record' },
            element: page(() => import('./pages/learner/Credential')),
          },
          {
            path: '/credential/:code/certificate',
            handle: { title: 'Certificate', detail: true },
            element: page(() => import('./pages/learner/Certificate')),
          },
          {
            path: '/recert',
            handle: { title: 'Annual renewal' },
            element: page(() => import('./pages/learner/Recert')),
          },
          {
            // The person's own account, which outlives any one programme (see `isPersonalRoute`).
            path: '/account',
            handle: { title: 'Your account', personal: true },
            element: page(() => import('./pages/learner/Account')),
          },
          {
            element: <SponsorArea />,
            children: [
              {
                path: '/sponsor',
                handle: { title: 'Sponsor dashboard' },
                element: page(() => import('./pages/sponsor/Dashboard')),
              },
              {
                path: '/sponsor/learners',
                handle: { title: 'Learners' },
                element: page(() => import('./pages/sponsor/Learners')),
              },
              {
                path: '/sponsor/learners/:userId',
                handle: { title: 'Learner record', detail: true },
                element: page(() => import('./pages/sponsor/Learner')),
              },
              {
                path: '/sponsor/brand',
                handle: { title: 'Academy brand' },
                element: page(() => import('./pages/sponsor/Brand')),
              },
              {
                path: '/sponsor/live-data',
                handle: { title: 'Live data' },
                element: page(() => import('./pages/sponsor/LiveData')),
              },
              {
                path: '/sponsor/settings',
                handle: { title: 'Academy settings' },
                element: page(() => import('./pages/sponsor/Settings')),
              },
            ],
          },
          {
            element: <AdminArea />,
            children: [
              {
                path: '/admin',
                handle: { title: 'Admin tenants' },
                element: page(() => import('./pages/admin/Tenants')),
              },
              {
                path: '/admin/tenants',
                handle: { title: 'Admin tenants' },
                element: page(() => import('./pages/admin/Tenants')),
              },
              {
                path: '/admin/tenants/:id',
                handle: { title: 'Tenant settings', detail: true },
                element: page(() => import('./pages/admin/Tenant')),
              },
              {
                path: '/admin/register',
                handle: { title: 'Credential register' },
                element: page(() => import('./pages/admin/Register')),
              },
              {
                path: '/admin/content',
                handle: { title: 'Content administration' },
                element: page(() => import('./pages/admin/Content')),
              },
              {
                path: '/admin/users',
                handle: { title: 'User administration' },
                element: page(() => import('./pages/admin/Users')),
              },
            ],
          },
        ],
      },
    ],
  },
]);
